import { useSelector } from 'react-redux';
import axios from 'axios';
import { ENDPOINT } from 'constants';

const API_BASE = `${ENDPOINT}api/v1/admin/question-answer`;
const API_ADMIN = `${ENDPOINT}api/v1/admin/`;

// Custom hook
export const useServicePlanApi = () => {
  const { user } = useSelector((state) => state.auth);
  const token = user?.accessToken;

  const getAuthHeaders = () => ({
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  // ==================== QUESTIONNAIRES API ====================
  const questionnairesApi = {
    getAll: () => axios.get(`${API_BASE}/questionnaires`, getAuthHeaders()),
    getById: (id) => axios.get(`${API_BASE}/questionnaires/${id}`, getAuthHeaders()),
    getByPageId: (pageId) => axios.get(`${API_BASE}/questionnaires/page/${pageId}`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/questionnaires`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/questionnaires/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/questionnaires/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
  };

  // ------------------- Questions API -------------------
  const questionsApi = {
    getAll: (questionnaireId) => {
      console.log('questionsApi.getAll called with questionnaireId:', questionnaireId);
      return axios.get(`${API_BASE}/questions`, {
        ...getAuthHeaders(),
        params: questionnaireId ? { questionnaireId } : {},
      });
    },
    getById: (id) => axios.get(`${API_BASE}/questions/${id}`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/questions`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/questions/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/questions/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
  };

  // ------------------- Answer Options API -------------------
  const answerOptionsApi = {
    getByQuestion: (questionId) => axios.get(`${API_BASE}/options/${questionId}`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/options`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/options/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/options/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
  };

  // ------------------- Features API -------------------
  const featuresApi = {
    getAll: () => axios.get(`${API_BASE}/features`, getAuthHeaders()),
    getById: (id) => axios.get(`${API_BASE}/features/${id}`, getAuthHeaders()),
    getByAnswer: (answerOptionId) => axios.get(`${API_BASE}/features/by-answer/${answerOptionId}`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/features`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/features/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/features/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
    attach: (data) => axios.post(`${API_BASE}/features/attach`, data, getAuthHeaders()),
    detach: (data) => axios.delete(`${API_BASE}/features/detach`, { ...getAuthHeaders(), data }),
    attachMultiple: (payload) => axios.post(`${API_BASE}/features/attach-multiple`, payload, getAuthHeaders()),
    detachMultiple: (payload) => axios.post(`${API_BASE}/features/detach-multiple`, payload, getAuthHeaders()),
  };

  // ✅ NEW: QA Feature Categories API
  const qaFeatureCategoriesApi = {
    getAll: () => axios.get(`${API_BASE}/qa-feature-categories`, getAuthHeaders()),
    getById: (id) => axios.get(`${API_BASE}/qa-feature-categories/${id}`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/qa-feature-categories`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/qa-feature-categories/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/qa-feature-categories/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
  };

  // ✅ NEW: QA Features API
  const qaFeaturesApi = {
    getAll: () => axios.get(`${API_BASE}/qa-features`, getAuthHeaders()),
    getById: (id) => axios.get(`${API_BASE}/qa-features/${id}`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/qa-features`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/qa-features/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/qa-features/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
  };

  const featureCategoriesApi = {
  getAll: () => axios.get(`${API_BASE}/qa-feature-categories`, getAuthHeaders()),
};

  // ------------------- Plans API -------------------
  const plansApi = {
    // getAll: () => axios.get(`${API_BASE}/plans`, getAuthHeaders()),
    getAll: (config = {}) => axios.get(`${API_BASE}/plans`, { 
  ...getAuthHeaders(), 
  ...config 
}),
    getById: (id) => axios.get(`${API_BASE}/plans/${id}`, getAuthHeaders()),
    getAllWithAddOns: () => axios.get(`${API_BASE}/plans/with-addons`, getAuthHeaders()),
    getByIdWithAddOns: (id) => axios.get(`${API_BASE}/plans/${id}/with-addons`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/plans`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/plans/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/plans/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
    addFeatures: (id, featureIds) => axios.post(`${API_BASE}/plans/${id}/features`, { featureIds }, getAuthHeaders()),

    removeFeatures: (planId, featureIds) => 
      axios.delete(`${API_BASE}/plans/${planId}/features`, { 
        ...getAuthHeaders(), 
        data: { featureIds } 
      }),
    addPrices: (planId, prices) => {
      console.log('API Call - Plan ID:', planId);
      console.log('API Call - Prices:', prices);
      return axios.post(`${API_BASE}/plans/${planId}/prices`, { prices }, getAuthHeaders());
    },
    updatePrice: (planId, priceId, data) => axios.put(`${API_BASE}/plans/${planId}/prices/${priceId}`, data, getAuthHeaders()),
    deletePrice: (planId, priceId) => axios.delete(`${API_BASE}/plans/${planId}/prices/${priceId}`, getAuthHeaders()),
    attachAddOns: (planId, addOnIds) => axios.post(`${API_BASE}/plans/${planId}/addons`, { addOnIds }, getAuthHeaders()),
    detachAddOn: (planId, addonId) => axios.delete(`${API_BASE}/plans/${planId}/addons/${addonId}`, getAuthHeaders()),
  };

  // ------------------- Add-ons API -------------------
  const addOnsApi = {
    getAll: () => axios.get(`${API_BASE}/addons`, getAuthHeaders()),
    getById: (id) => axios.get(`${API_BASE}/addons/${id}`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/addons`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/addons/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/addons/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
    getPrices: (id) => axios.get(`${API_BASE}/addons/${id}/prices`, getAuthHeaders()),
    addPrice: (addonId, data) => axios.post(`${API_BASE}/addons/${addonId}/prices`, data, getAuthHeaders()),
    updatePrice: (addonId, priceId, data) => axios.put(`${API_BASE}/addons/${addonId}/prices/${priceId}`, data, getAuthHeaders()),
    deletePrice: (addonId, priceId) => axios.delete(`${API_BASE}/addons/${addonId}/prices/${priceId}`, getAuthHeaders()),
  };

  // ------------------- Mappings API -------------------
  const mappingsApi = {
    getAll: () => axios.get(`${API_BASE}/mappings`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/plans/mappings`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/plans/mappings/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/mappings/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
    // JSON file in the import format (current mappings, or one example entry).
    exportJson: (questionnaireId) =>
      axios.get(`${API_BASE}/mappings/export`, { ...getAuthHeaders(), params: { questionnaireId } }),
    // mode: 'replace' (file = full set, others deleted) | 'add' (add + update only)
    importJson: (questionnaireId, mode, data) =>
      axios.post(`${API_BASE}/mappings/import`, { questionnaireId, mode, data }, getAuthHeaders()),
  };

  // ------------------- Categories API -------------------
  const categoriesApi = {
    getAll: () => axios.get(`${API_BASE}/categories`, getAuthHeaders()),
    create: (data) => axios.post(`${API_BASE}/categories`, data, getAuthHeaders()),
    update: (id, data) => axios.put(`${API_BASE}/categories/${id}`, data, getAuthHeaders()),
    delete: (id, label = '') =>
      axios.delete(`${API_BASE}/categories/${id}?label=${encodeURIComponent(label)}`, getAuthHeaders()),
    getById: (id) => axios.get(`${API_BASE}/categories/${id}`, getAuthHeaders()),
  };

  const currencyApi = {
    getAll: () => axios.get(`${API_ADMIN}/currencies`, getAuthHeaders()),
    getById: (id) => axios.get(`${API_ADMIN}/currencies/${id}`, getAuthHeaders()),
    getMultipliers: () => axios.get(`${API_ADMIN}/currency-multipliers`, getAuthHeaders()),
  };

  return {
    questionnairesApi,
    questionsApi,
    answerOptionsApi,
    featuresApi,
    // ✅ NEW: QA Feature APIs
    qaFeatureCategoriesApi,
    qaFeaturesApi,
    plansApi,
    addOnsApi,
    mappingsApi,
    categoriesApi,
    currencyApi,
    featureCategoriesApi
  };
};
